import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProductsByCategory, setFilters, setPage } from '../store/slices/productSlice';
import Loader from '../components/common/loader';
import AddToCartButton from '../components/cart/addToCartButton';
import WishlistButton from '../components/wishlist/wishlistButton';

const CategoryProducts = () => {
  const { category } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { categoryProducts, pagination, loading, error } = useSelector(state => state.products);

  const [sortOption, setSortOption] = useState('createdAt_desc');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
    const [filters, setLocalFilters] = useState({
    subCategory: '',
    material: '',
    color: '',
    courierBagType: '',
    paperBagType: ''
  });

  useEffect(() => {
    // Parse query params
    const queryParams = new URLSearchParams(location.search);
    const page = parseInt(queryParams.get('page')) || 1;
    const minPrice = queryParams.get('minPrice') || '';
    const maxPrice = queryParams.get('maxPrice') || '';
    const sortValue = queryParams.get('sort') || 'createdAt_desc';
     const subCategory = queryParams.get('subCategory') || '';
    const material = queryParams.get('material') || '';
    const color = queryParams.get('color') || '';
    const courierBagType = queryParams.get('courierBagType') || '';
    const paperBagType = queryParams.get('paperBagType') || '';
    
    setSortOption(sortValue);
    setPriceRange({ min: minPrice, max: maxPrice });
    setLocalFilters({ subCategory, material, color, courierBagType, paperBagType });
    
    const [sortBy, sortOrder] = sortValue.split('_');
    dispatch(setFilters({ sortBy, sortOrder, minPrice, maxPrice, subCategory, material, color, courierBagType, paperBagType }));
    dispatch(setPage(page));
    
    const params = {
      page,
      sortBy,
      sortOrder,
      minPrice: minPrice || undefined,
      maxPrice: maxPrice || undefined,
      subCategory: subCategory || undefined,
      material: material || undefined,
      color: color || undefined,
      courierBagType: courierBagType || undefined,
      paperBagType: paperBagType || undefined
    };
    
    dispatch(fetchProductsByCategory({ category, params }));
  }, [dispatch, category, location.search]);

  // Get formatted category name
  const getCategoryName = () => {
    return category
      .replace(/_/g, ' ')
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Format name helper
  const formatName = (name) => {
    return name?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';
  };

  // Handle sort change
  const handleSortChange = (e) => {
    const newSortOption = e.target.value;
    setSortOption(newSortOption);
    applyFilters({ sort: newSortOption });
  };

  // Handle price range change
  const handlePriceChange = (e) => {
    const { name, value } = e.target;
    setPriceRange(prev => ({ ...prev, [name]: value }));
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setLocalFilters(prev => ({ ...prev, [name]: value }));
  };

  // Apply filters and update URL
  const applyFilters = (newFilters = {}) => {
    const queryParams = new URLSearchParams(location.search);
    
    // Update sort
    if (newFilters.sort) {
      queryParams.set('sort', newFilters.sort);
    }
    
    // Update price range
    if (newFilters.applyPrice || newFilters.applyAll) {
      if (priceRange.min) queryParams.set('minPrice', priceRange.min);
      else queryParams.delete('minPrice');
      
      if (priceRange.max) queryParams.set('maxPrice', priceRange.max);
      else queryParams.delete('maxPrice');
    }

    // Update other filters
    if (newFilters.applyAll) {
       if (filters.subCategory) queryParams.set('subCategory', filters.subCategory);
      else queryParams.delete('subCategory');

      if (filters.material) queryParams.set('material', filters.material);
      else queryParams.delete('material');

      if (filters.color) queryParams.set('color', filters.color);
      else queryParams.delete('color');

      if (filters.courierBagType) queryParams.set('courierBagType', filters.courierBagType);
      else queryParams.delete('courierBagType');

      if (filters.paperBagType) queryParams.set('paperBagType', filters.paperBagType);
      else queryParams.delete('paperBagType');
    }
    
    
    // Reset to page 1 when filters change
    queryParams.set('page', 1);
    
    // Update URL
    navigate(`/category/${category}?${queryParams.toString()}`);
  };

  // Clear all filters
  const clearFilters = () => {
    setSortOption('createdAt_desc');
    setPriceRange({ min: '', max: '' });
     setLocalFilters({ subCategory: '', material: '', color: '', courierBagType: '', paperBagType: '' });

    navigate(`/category/${category}`);
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('page', newPage);
    navigate(`/category/${category}?${queryParams.toString()}`);
  };

  // Calculate discount percentage
  const calculateDiscount = (mrp, price) => {
    if (!mrp || !price || mrp <= price) return 0;
    return Math.round(((mrp - price) / mrp) * 100);
  };

  // Get subcategories based on category
  const getSubCategories = () => {
    if (category === 'AGRICULTURE_PRODUCTS') {
      return [
        'MULCHING_FILM', 'NURSERY_SHEETS'
      ];
    } else if (category === 'PACKAGING_PRODUCTS') {
      return [
        'COURIER_BAGS', 'PAPER_BAGS'
      ];
    }
    return [];
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Breadcrumbs */}
      <div className="mb-6">
        <nav className="text-sm">
          <ol className="list-none p-0 inline-flex">
            <li className="flex items-center">
              <Link to="/" className="text-gray-500 hover:text-blue-600">Home</Link>
              <svg className="w-3 h-3 mx-2" fill="currentColor" viewBox="0 0 12 12">
                <path d="M9.707 5.293l-5-5a1 1 0 00-1.414 1.414L7.586 6 3.293 10.293a1 1 0 101.414 1.414l5-5a1 1 0 000-1.414z" />
              </svg>
            </li>
            <li className="flex items-center">
              <Link to="/products" className="text-gray-500 hover:text-blue-600">Products</Link>
              <svg className="w-3 h-3 mx-2" fill="currentColor" viewBox="0 0 12 12">
                <path d="M9.707 5.293l-5-5a1 1 0 00-1.414 1.414L7.586 6 3.293 10.293a1 1 0 101.414 1.414l5-5a1 1 0 000-1.414z" />
              </svg>
            </li>
            <li>
              <span className="text-gray-700">{getCategoryName()}</span>
            </li>
          </ol>
        </nav>
      </div>

      <h1 className="text-2xl font-bold mb-6">{getCategoryName()}</h1>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Filters sidebar */}
        <div className="w-full md:w-1/4 bg-white rounded-lg shadow p-4">
          <h2 className="text-xl font-semibold mb-4">Filters</h2>
          
          <div className="space-y-4">
            {/* Sub-Category filter - NEW */}
            {getSubCategories().length > 0 && (
              <div>
                <label className="block text-gray-700 mb-2">Sub-Category</label>
                <select
                  name="subCategory"
                  value={filters.subCategory}
                  onChange={handleFilterChange}
                  className="w-full border rounded p-2"
                >
                  <option value="">All Sub-Categories</option>
                  {getSubCategories().map(sub => (
                    <option key={sub} value={sub}>
                      {formatName(sub)}
                    </option>
                  ))}
                        </select>
              </div>
            )}

            {/* Courier Bag Type filter */}
            {filters.subCategory === 'COURIER_BAGS' && (
              <div>
                <label className="block text-gray-700 mb-2">Bag Type</label>
                <select
                  name="courierBagType"
                  value={filters.courierBagType || ''}
                  onChange={handleFilterChange}
                  className="w-full border rounded p-2"
                >
                  <option value="">All Types</option>
                  <option value="POD">POD (Proof of Delivery)</option>
                  <option value="NON_POD">Non-POD</option>
                </select>
              </div>
            )}

            {/* Paper Bag Type filter */}
            {filters.subCategory === 'PAPER_BAGS' && (
              <div>
                <label className="block text-gray-700 mb-2">Bag Type</label>
                <select
                  name="paperBagType"
                  value={filters.paperBagType || ''}
                  onChange={handleFilterChange}
                  className="w-full border rounded p-2"
                >
                  <option value="">All Types</option>
                  <option value="SOS_PAPER_BAGS_WHITE">SOS Paper Bags (White)</option>
                  <option value="SOS_PAPER_BAGS_BROWN">SOS Paper Bags (Brown)</option>
                  <option value="TWISTED_HANDLE_BROWN">Twisted Handle (Brown)</option>
                  <option value="TWISTED_HANDLE_WHITE">Twisted Handle (White)</option>
                </select>
              </div>
            )}
            {/* Material filter - NEW */}
            <div>
              <label className="block text-gray-700 mb-2">Material</label>
              <select
                name="material"
                value={filters.material}
                onChange={handleFilterChange}
                className="w-full border rounded p-2"
              >
                <option value="">All Materials</option>
                <option value="LLDPE">LLDPE</option>
                <option value="LDPE">LDPE</option>
                <option value="KRAFT_PAPER">Kraft Paper</option>
                <option value="BLEACHED_PAPER">Bleached Paper</option>
              </select>
            </div>

            {/* Color filter - NEW */}
            <div>
              <label className="block text-gray-700 mb-2">Color</label>
              <select
                name="color"
                value={filters.color}
                onChange={handleFilterChange}
                className="w-full border rounded p-2"
              >
                 <option value="">All Colors</option>
                <option value="WHITE_AND_BLACK">White and Black</option>
                <option value="SILVER_AND_BLACK">Silver and Black</option>
                <option value="TRANSPARENT">Transparent</option>
                <option value="BROWN">Brown</option>
                <option value="WHITE">White</option>
              </select>
            </div>

            {/* Price range */}
            <div>
              <label className="block text-gray-700 mb-2">Price Range</label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  name="min"
                  placeholder="Min"
                  value={priceRange.min}
                  onChange={handlePriceChange}
                  className="w-1/2 border rounded p-2"
                />
                <span>-</span>
                <input
                  type="number"
                  name="max"
                  placeholder="Max"
                  value={priceRange.max}
                  onChange={handlePriceChange}
                  className="w-1/2 border rounded p-2"
                />
              </div>
            </div>
            
            {/* Sort options */}
            <div>
              <label className="block text-gray-700 mb-2">Sort By</label>
              <select
                value={sortOption}
                onChange={handleSortChange}
                className="w-full border rounded p-2"
              >
                <option value="createdAt_desc">Newest First</option>
                <option value="createdAt_asc">Oldest First</option>
                <option value="sellingPrice_asc">Price: Low to High</option>
                <option value="sellingPrice_desc">Price: High to Low</option>
                <option value="discount_desc">Discount: High to Low</option>
              </select>
            </div>
            
            <div className="flex space-x-2 pt-2">
              <button
                onClick={() => applyFilters({ applyAll: true })}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition w-full"
              >
                Apply Filters
              </button>
              <button
                onClick={clearFilters}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 transition"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
        
        {/* Product grid */}
        <div className="w-full md:w-3/4">
          {loading.categoryProducts ? (
            <div className="flex justify-center items-center h-64">
              <Loader size="lg" />
            </div>
          ) : error.categoryProducts ? (
            <div className="bg-red-100 text-red-700 p-4 rounded">
              {error.categoryProducts}
            </div>
          ) : !categoryProducts[category] || categoryProducts[category].length === 0 ? (
            <div className="bg-yellow-100 text-yellow-800 p-4 rounded">
              No products found in this category.
            </div>
          ) : (
            <>
              {/* Results count */}
              <div className="mb-4 text-gray-600">
                Showing {categoryProducts[category].length} products
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryProducts[category].map(product => (
                  <div key={product.productId} className="bg-white rounded-lg shadow overflow-hidden hover:shadow-md transition">
                    <div className="relative">
                      <Link to={`/product/${product.productId}`}>
                        <div className="h-48 overflow-hidden">
                          <img 
                            src={product.imageUrl} 
                            alt={product.name} 
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      </Link>

                      {/* Stock Badge - NEW */}
                      {product.stockStatus === 'LOW_STOCK' && (
                        <span className="absolute top-2 left-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded font-medium">
                          Low Stock
                        </span>
                      )}
                      {product.stockStatus === 'OUT_OF_STOCK' && (
                        <span className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded font-medium">
                          Out of Stock
                        </span>
                      )}

                      {/* Eco-Friendly Badge - NEW */}
                      {product.isEcoFriendly && (
                        <span className="absolute top-2 right-10 bg-green-500 text-white text-xs px-2 py-1 rounded font-medium">
                          🌱 Eco
                        </span>
                      )}

                      <div className="absolute top-2 right-2">
                        <WishlistButton productId={product.productId} />
                      </div>
                    </div>
                    
                    <Link to={`/product/${product.productId}`}>
                      <div className="p-4">
                        <h3 className="font-medium text-gray-900 mb-1 truncate">
                          {product.name}
                        </h3>

                        {product.manufacturer && (
                          <p className="text-xs text-gray-500 mb-2">{product.manufacturer}</p>
                        )}

                        {/* Material & Color - NEW */}
                        <div className="flex gap-2 mb-2">
                          {product.material && (
                            <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                              {formatName(product.material)}
                            </span>
                          )}
                          {product.color && (
                            <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                              {formatName(product.color)}
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-end mb-2">
                          <span className="text-lg font-bold text-gray-900">
                            ₹{product.price}
                          </span>
                          
                          {product.mrp > product.price && (
                            <>
                              <span className="text-sm text-gray-500 line-through ml-2">
                                ₹{product.mrp}
                              </span>
                              
                              <span className="text-sm text-green-600 ml-2">
                                {calculateDiscount(product.mrp, product.price)}% off
                              </span>
                            </>
                          )}
                        </div>

                        {/* Bulk Pricing Indicator - NEW */}
                        {product.hasBulkPricing && (
                          <p className="text-xs text-blue-600 mb-2">
                            💰 Bulk pricing from {product.bulkStartsFrom}+ units
                          </p>
                        )}

                        {/* Min Order Quantity - NEW */}
                        {product.minOrderQuantity > 1 && (
                          <p className="text-xs text-gray-500">
                            Min Order: {product.minOrderQuantity} units
                          </p>
                        )}
                      </div>
                    </Link>
                    
                    <div className="px-4 pb-4">
                      <AddToCartButton 
                        product={product}
                        fullWidth={true}
                        showQuantity={false}
                        disabled={product.stockStatus === 'OUT_OF_STOCK'}
                      />
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="flex justify-center mt-8">
                  <div className="flex space-x-1">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className={`px-3 py-1 rounded ${
                        pagination.page === 1
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Previous
                    </button>
                    
                    {[...Array(pagination.pages)].map((_, i) => (
                      <button
                        key={i}
                        onClick={() => handlePageChange(i + 1)}
                        className={`px-3 py-1 rounded ${
                          pagination.page === i + 1
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                    
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.pages}
                      className={`px-3 py-1 rounded ${
                        pagination.page === pagination.pages
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default CategoryProducts;